/**
 * Demo companies (#67-lite / company-scoped demo) — each selectable company maps
 * 1:1 to a demo pack and sits under a DEMO_PROFILE industry. Picking a company on
 * the homepage scopes the management context to it:
 *   - org-level data (Org Directory, cost centers, …) filters by `orgNames`
 *     (the `users.organization` values that belong to the company)
 *   - portfolio-level data follows `rootPortfolioId` (the existing portfolio scope)
 *
 * NOTE: one demo per company (decided 2026-06-20). Gentex is a single demo whose
 * "Gentex Corporation" portfolio tree carries both the defense/optics value streams
 * AND the re-parented Commercial Aerospace (Windows) portfolio (migration 00362) —
 * the former gentex-aerospace + gentex-valuestreams demos merged into one pack.
 */
export interface DemoCompany {
  /** Matches the demo-pack id. */
  id: string
  name: string
  /** DEMO_PROFILE id (industry grouping on the homepage). */
  profileId: string
  blurb: string
  /** `users.organization` values that belong to this company (org-level scope). */
  orgNames: string[]
  /** Root portfolio selected when the company is chosen (portfolio-level scope). */
  rootPortfolioId: string
  /**
   * The `organizations.id` root that this company's demo persona cast is tagged
   * to (`users.primary_organization_id`). Set by the per-company role-cast
   * migrations (Gentex 00364, ConstructCo 00365, Boeing 00366). Drives the
   * persona-switcher / Settings cast scoping: a persona belongs to a company's
   * cast iff its `primaryOrganizationId` equals this. The broader org roster
   * carries `primary_organization_id = null`, so this cleanly isolates the cast.
   */
  castOrgId?: string
  /** Native currency the company's seeded figures are denominated in (ISO code).
   *  Defaults to USD when omitted. Drives the display-currency context so the app
   *  renders this company's money in its own currency (e.g. Codan = AUD → A$),
   *  with an optional user override + conversion (see appStore displayCurrencyOverride). */
  currency?: string
  /** Featured = rendered with a standout highlight in the homepage picker.
   *  Mach12 (the platform's own org / catch-all for generic demo data) is featured. */
  featured?: boolean
}

export const DEMO_COMPANIES: DemoCompany[] = [
  {
    id: 'mach12',
    name: 'Mach12',
    profileId: 'technology-digital',
    blurb: 'The Tesseract platform org — generic demo personas + shared fixtures (Volume Test) not owned by any customer company. The catch-all home for hanging demo data.',
    orgNames: ['Mach12'],
    rootPortfolioId: 'port_mach12_root',
    castOrgId: 'org_mach12',
    featured: true,
  },
  {
    id: 'ec-enterprise',
    name: 'ConstructCo Engineering',
    profileId: 'engineering-construction',
    blurb: 'Highway & bridge, commercial buildings, facilities, and environmental programs',
    orgNames: ['ConstructCo Engineering'],
    rootPortfolioId: 'port_root',
    castOrgId: 'org_constructco_corp',
  },
  {
    id: 'gentex',
    name: 'Gentex',
    profileId: 'aerospace-defense',
    blurb: 'Defense/optics value streams (helmets, respiratory, optics) + commercial aerospace dimmable windows (787-10 / A321XLR)',
    orgNames: ['Gentex', 'Gentex UK'],
    rootPortfolioId: 'port_gtxvs_root',
    castOrgId: 'org_gtxvs_corp',
  },
  {
    id: 'boeing-mdc2',
    name: 'Boeing MDC2 (Defense)',
    profileId: 'aerospace-defense',
    blurb: 'Federal defense & aerospace contracting programs',
    orgNames: ['Boeing'],
    rootPortfolioId: 'port_defense_aero',
    castOrgId: 'org_boeing_corp',
  },
  {
    id: 'codan',
    name: 'Codan Limited',
    profileId: 'aerospace-defense',
    blurb: 'Australian defense, public-safety & mining electronics — tactical HF radio (Sentry-H), Zetron mission-critical C2 / land mobile radio, and Minelab next-gen detection. Programs in AUD.',
    orgNames: ['Codan', 'Codan Limited'],
    rootPortfolioId: 'port_codan_root',
    castOrgId: 'org_codan_corp',
    currency: 'AUD',
  },
]

/** The native currency a demo company's figures are seeded in (ISO code). USD default. */
export function demoCompanyCurrency(companyId: string | null | undefined): string {
  return getDemoCompany(companyId)?.currency ?? 'USD'
}

/**
 * The set of all demo-cast root org ids (`users.primary_organization_id` values
 * that mark a persona as part of a company's role cast). DB-driven replacement
 * for the old hardcoded `DEMO_PERNRS` allowlist — the per-company cast migrations
 * tag exactly these personas, and the broader org roster carries a null
 * `primary_organization_id`, so this set isolates the cast cleanly.
 */
export const CAST_ORG_IDS: ReadonlySet<string> = new Set(
  DEMO_COMPANIES.map((c) => c.castOrgId).filter((x): x is string => x != null),
)

/**
 * True when a persona's `primary_organization_id` marks it as part of a demo
 * company's role cast (i.e. it should appear in the persona switcher / Settings
 * picker). Replaces the per-component hardcoded pernr allowlists.
 */
export function isDemoCastOrg(primaryOrganizationId: string | null | undefined): boolean {
  return primaryOrganizationId != null && CAST_ORG_IDS.has(primaryOrganizationId)
}

export function companiesForProfile(profileId: string): DemoCompany[] {
  return DEMO_COMPANIES.filter((c) => c.profileId === profileId)
}

export function getDemoCompany(id: string | null | undefined): DemoCompany | undefined {
  return id ? DEMO_COMPANIES.find((c) => c.id === id) : undefined
}

/**
 * Reverse lookup: the demo company whose role cast lives in `castOrgId` (a
 * persona's `primary_organization_id`). Lets the persona switcher align the
 * active company context to the persona you picked, so a refresh restores a
 * consistent (company + persona) pair. Undefined for personas with no cast org
 * (e.g. the mock-mode roster).
 */
export function getDemoCompanyByCastOrg(castOrgId: string | null | undefined): DemoCompany | undefined {
  return castOrgId ? DEMO_COMPANIES.find((c) => c.castOrgId === castOrgId) : undefined
}

/**
 * Filter a flat portfolio list to the selected company's subtree (its root
 * portfolio + all descendants, by `parentId`). Returns the list unchanged when
 * no company is scoped or the id is unknown. Each company is a clean top-level
 * tree (see migration 00335), so subtrees don't overlap.
 */
export function scopePortfoliosToCompany<T extends { id: string; parentId?: string | null }>(
  flat: T[],
  companyId: string | null | undefined,
): T[] {
  const company = getDemoCompany(companyId)
  if (!company) return flat
  const childrenByParent = new Map<string, T[]>()
  for (const p of flat) {
    const pid = p.parentId ?? null
    if (pid) {
      const arr = childrenByParent.get(pid) ?? []
      arr.push(p)
      childrenByParent.set(pid, arr)
    }
  }
  const keep = new Set<string>()
  const stack: string[] = [company.rootPortfolioId]
  while (stack.length) {
    const id = stack.pop()!
    if (keep.has(id)) continue
    keep.add(id)
    for (const c of childrenByParent.get(id) ?? []) stack.push(c.id)
  }
  return flat.filter((p) => keep.has(p.id))
}

/**
 * The set of portfolio ids belonging to any NON-featured demo company's subtree
 * (each company's root portfolio + descendants). This is the "demo data" set the
 * Sandbox must hide: demo companies (Gentex, Boeing, ConstructCo, Codan) are
 * reached via the demo profiles, not the Sandbox. Mach12 is `featured` (the
 * platform org), so its subtree is intentionally NOT included — it stays visible
 * in the Sandbox alongside genuine sandbox-built portfolios (which sit under
 * their own roots, not under any demo company).
 */
export function demoCompanyPortfolioIds<T extends { id: string; parentId?: string | null }>(
  flat: T[],
): Set<string> {
  const ids = new Set<string>()
  for (const company of DEMO_COMPANIES) {
    if (company.featured) continue
    for (const p of scopePortfoliosToCompany(flat, company.id)) ids.add(p.id)
  }
  return ids
}

/**
 * Filter a project list to the selected company. A project belongs to the company
 * when its `portfolioId` falls inside that company's portfolio subtree — the same
 * scope `scopePortfoliosToCompany` computes, so projects and portfolios agree on
 * "what belongs to this company". Returns the list unchanged when no company is
 * scoped or the id is unknown. Projects with no `portfolioId` are excluded once a
 * company is selected (they can't be attributed to a company tree).
 */
export function scopeProjectsToCompany<T extends { portfolioId?: string | null }>(
  projects: T[],
  portfolios: Array<{ id: string; parentId?: string | null }>,
  companyId: string | null | undefined,
): T[] {
  const company = getDemoCompany(companyId)
  if (!company) return projects
  const inCompany = new Set(scopePortfoliosToCompany(portfolios, companyId).map((p) => p.id))
  return projects.filter((p) => p.portfolioId != null && inCompany.has(p.portfolioId))
}

/**
 * Filter a user/person list to the selected company by `organization` — the
 * `users.organization` employer label, which is the dimension `orgNames` carries
 * (e.g. ['Gentex', 'Gentex UK']). Returns the list unchanged when no company is
 * scoped or the id is unknown. Users with no `organization` are excluded once a
 * company is selected. (NOTE: this keys on the employer-name text, NOT the
 * `organizations` table id — the two identity spaces don't align yet.)
 */
export function scopeUsersToCompany<T extends { organization?: string | null }>(
  users: T[],
  companyId: string | null | undefined,
): T[] {
  const company = getDemoCompany(companyId)
  if (!company) return users
  const orgNames = company.orgNames
  return users.filter((u) => u.organization != null && orgNames.includes(u.organization))
}
